<template>
  <button
    class="flex disabled:opacity-60 p-3"
    :disabled="disabled"
    @click="$emit('click')"
  >
    <div
      v-if="hasIcon"
      class="aspect-square rounded-full text-white inline-flex items-center justify-center"
      :class="[
        iconClass,
        sizeClass.icon,
        getIconBackgroundColor,
        { 'order-3': iconSide === 'right' }
      ]"
    >
      <fa
        v-if="icon"
        :icon="icon"
        :size="!$slots['next-to-label'] && !$slots['below-label'] ? 'lg' : 'md'"
      />
      <slot
        v-else
        name="icon"
      />
    </div>

    <div
      class="inline-flex items-center w-full order-2 min-h-full"
      :class="{
        'text-gray-500': muted,
        [sizeClass.space]: hasIcon
      }"
    >
      <div class="flex flex-col items-start w-full text-left">
        <div
          class="flex w-full"
          :class="labelClass"
        >
          <span
            :label-i18n="labelI18n"
            class="font-secondary"
          >
            {{ label }}
          </span>
          <slot name="next-to-label" />
        </div>

        <slot name="below-label" />
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
const { $isSol } = useNuxtApp()

const props = withDefaults(
  defineProps<{
    label: string
    value: string
    icon: string | object
    hideIcon?: boolean
    size?: 'sm' | 'lg'
    labelI18n?: string | null
    muted?: boolean
    iconSide?: 'left' | 'right'
    iconClass?: string | null
    labelClass?: string | null
    disabled?: boolean
  }>(),
  {
    size: 'sm',
    labelI18n: null,
    muted: false,
    iconSide: 'left',
    hideIcon: false,
    iconClass: null,
    labelClass: null,
    disabled: false,
  }
)

defineEmits(['click'])

const slots = useSlots()

const sizeClass = computed(() => {
  switch (props.size) {
    case 'lg':
      return {
        icon: 'h-12',
        space: props.iconSide === 'left'
          ? 'ml-3'
          : 'mr-3',
      }
    default:
      return {
        icon: 'h-10',
        space: props.iconSide === 'left'
          ? 'ml-3'
          : 'mr-3',
      }
  }
})

const getIconBackgroundColor = computed(() => {
  if (props.muted) {
    return 'bg-gray-500'
  }
  return $isSol ? 'bg-blue' : 'bg-blue-800'
})

const hasIcon = computed(() => (
  (props.icon || slots.icon) &&
  !props.hideIcon
))
</script>